import { useState } from 'react';
import { Link } from 'react-router-dom';
import { interiorImages, exteriorImages } from '../../data/homeImages';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { ArrowRight } from '../ui/Icons';

type Shade = { name: string; hex: string; code: string };
type Family = 'interior' | 'exterior';

/**
 * Swatches are rendered from hex values rather than images, so the palette
 * stays crisp at any size and costs nothing to load.
 */
const PALETTES: Record<Family, { blurb: string; image: string; alt: string; link: string; shades: Shade[] }> = {
  interior: {
    blurb: 'Soft, liveable tones engineered for washability and a smooth, low-glare finish that flatters indoor light.',
    image: interiorImages.swatchRoom,
    alt: 'Interior wall painted in a soft Vertex shade beside a swatch fan',
    link: '/InteriorWallPaint/Shades',
    shades: [
      { name: 'Warm Linen', hex: '#EFE7D6', code: 'VP-1042' },
      { name: 'Clay Rose', hex: '#D9A79A', code: 'VP-2318' },
      { name: 'Sage Mist', hex: '#A8BFA6', code: 'VP-3701' },
      { name: 'Deep Indigo', hex: '#33427E', code: 'VP-4460' },
      { name: 'Terracotta', hex: '#C4653F', code: 'VP-2205' },
      { name: 'Slate Grey', hex: '#6E7480', code: 'VP-5112' },
    ],
  },
  exterior: {
    blurb: 'UV-stable, weather-tough shades that hold their depth through monsoon and summer without chalking or fading.',
    image: exteriorImages.banner,
    alt: 'House elevation painted in a Vertex exterior shade',
    link: '/ExteriorWallPaint',
    shades: [
      { name: 'Ivory Stone', hex: '#EDE4D3', code: 'VP-6014' },
      { name: 'Desert Sand', hex: '#D8B98C', code: 'VP-6220' },
      { name: 'Ochre Gold', hex: '#C99141', code: 'VP-6338' },
      { name: 'Forest Slate', hex: '#4F6355', code: 'VP-7104' },
      { name: 'Brick Earth', hex: '#9E5540', code: 'VP-6612' },
      { name: 'Storm Blue', hex: '#3E5C77', code: 'VP-7420' },
    ],
  },
};

const ExploreColours = () => {
  const [tab, setTab] = useState<Family>('interior');
  const active = PALETTES[tab];

  return (
    <section className="vp-section bg-white">
      <div className="container">
        <SectionHeading
          eyebrow="Explore colours"
          title="Find the shade your room has been waiting for"
          lead="Browse curated interior and exterior families, then see how each one behaves on a real wall before you commit."
          className="mb-10"
          action={
            <div className="vp-tabs" role="tablist" aria-label="Colour families">
              {(['interior', 'exterior'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`colours-tab-${key}`}
                  aria-selected={tab === key}
                  aria-controls="colours-panel"
                  onClick={() => setTab(key)}
                  className="vp-tab capitalize"
                >
                  {key}
                </button>
              ))}
            </div>
          }
        />

        {/* Body */}
        <div key={tab} id="colours-panel" role="tabpanel" aria-labelledby={`colours-tab-${tab}`} className="vp-fade grid lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
          {/* Preview */}
          <div className="lg:col-span-2 relative rounded-2xl overflow-hidden min-h-[280px] group bg-brand-navy">
            <img src={active.image} alt={active.alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e1330]/85 via-[#0e1330]/25 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
              <h3 className="text-2xl font-bold text-white capitalize">{tab} palette</h3>
              <p className="mt-2 text-sm text-white/80 leading-relaxed">{active.blurb}</p>
              <Link to={active.link} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-gold hover:gap-3 transition-all">
                View {tab} range
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Swatches */}
          <ul className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {active.shades.map((s, i) => (
              <Reveal as="li" key={s.code} delay={Math.min(i, 4) as 0 | 1 | 2 | 3 | 4}>
                <Link to={active.link} className="vp-swatch block" title={`${s.name} — ${s.code}`}>
                  <div className="vp-swatch-color h-24 sm:h-28" style={{ backgroundColor: s.hex }} aria-hidden="true" />
                  <div className="p-3.5">
                    <p className="text-sm font-semibold text-brand-ink truncate">{s.name}</p>
                    <p className="mt-0.5 text-xs text-brand-muted tracking-wide">{s.code}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>

        {/* Footer CTA */}
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/colours/catalogue" className="vp-btn vp-btn-primary">
            Browse full colour catalogue
            <ArrowRight size={16} />
          </Link>
          <Link to="/colours/combinations" className="vp-btn vp-btn-outline">
            See colour combinations
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ExploreColours;
